/**
 * Calls-tab feed — call history from the SDK call-log store (NOT chat messages).
 *
 *   const { calls, hasMore, fetchMore, isLoading, error, refresh } = useCallList()
 *
 * Records are written server-authoritatively by signaling on call termination
 * and read here through the SDK (`sdk.calls.history()`) — the app never touches
 * the endpoint itself. Each row's `peerId` is the other party's
 * contactPresenceId — the call-back target. Held in local state (this feed is
 * independent of the chat message store).
 *
 * Live-ish: when an in-progress call ends (the calls store's `active` clears) we
 * refresh shortly after, so a just-finished call appears without a manual reload.
 * The feed is EMPTY until calls are actually placed (nothing to show before).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CallRecord } from '../types'
import type { CallRecord as SdkCall } from '@/sdk/instavcMessenger'
import { fetchCallHistory } from '@/sdk/instavcMessenger-instance'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { useCallsStore } from '../store/calls.store'

interface UseCallList {
  calls: CallRecord[]
  hasMore: boolean
  isLoading: boolean
  error: string | null
  fetchMore: () => Promise<void>
  refresh: () => Promise<void>
}

const PAGE_SIZE = 50

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const isYesterday = d.toDateString() === yesterday.toDateString()
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  if (sameDay) return t
  if (isYesterday) return `Yesterday, ${t}`
  return `${d.toLocaleDateString()}, ${t}`
}

function formatDuration(ms: number): string | undefined {
  if (!ms || ms <= 0) return undefined
  const s = Math.round(ms / 1000)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`
}

function toRecord(wc: SdkCall): CallRecord {
  return {
    id: wc.id,
    name: wc.peerName || `User ${wc.peerId.slice(0, 8)}`,
    imageUrl: wc.peerAvatarUrl ?? undefined,
    isVideoCall: wc.callType === 'video',
    isMissedCall: wc.missed,
    callDir: wc.direction === 'outgoing' ? 'out' : 'in',
    time: formatTime(wc.createdAt),
    duration: formatDuration(wc.durationMs),
    peerId: wc.peerId,
  }
}

export function useCallList(): UseCallList {
  const myUserId = useAuthStore((s) => s.user?.id)
  const [calls, setCalls] = useState<CallRecord[]>([])
  const [cursor, setCursor] = useState<{ nextCursor: string | null; hasMore: boolean }>({ nextCursor: null, hasMore: false })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seededRef = useRef(false)

  const load = useCallback(
    async (mode: 'replace' | 'append', before?: string) => {
      if (!myUserId) return
      setIsLoading(true)
      setError(null)
      try {
        const page = await fetchCallHistory(myUserId, { limit: PAGE_SIZE, before })
        const rows = page.items.map(toRecord)
        setCalls((prev) => (mode === 'replace' ? rows : [...prev, ...rows]))
        setCursor({ nextCursor: page.nextCursor, hasMore: page.hasMore })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'failed to load calls')
        if (mode === 'replace') seededRef.current = false
      } finally {
        setIsLoading(false)
      }
    },
    [myUserId],
  )

  // Initial seed — once per signed-in user.
  useEffect(() => {
    if (!myUserId) return
    if (seededRef.current) return
    seededRef.current = true
    void load('replace')
  }, [myUserId, load])

  // Refresh shortly after an in-progress call ends (record is written async).
  const active = useCallsStore((s) => s.active)
  const prevActive = useRef(active)
  useEffect(() => {
    if (prevActive.current && !active) {
      const t = setTimeout(() => void load('replace'), 1200)
      prevActive.current = active
      return () => clearTimeout(t)
    }
    prevActive.current = active
  }, [active, load])

  const fetchMore = useCallback(async () => {
    if (!cursor.hasMore || !cursor.nextCursor || isLoading) return
    await load('append', cursor.nextCursor)
  }, [cursor.hasMore, cursor.nextCursor, isLoading, load])

  const refresh = useCallback(() => load('replace'), [load])

  return { calls, hasMore: cursor.hasMore, isLoading, error, fetchMore, refresh }
}
